<script setup lang="ts">
import { ref } from 'vue';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/vue/24/outline';

// Small icon-only copy affordance matching the 2a design's identity-
// comparison card (a copy icon next to every id/name/date/etc. value) —
// missing entirely from the workbench before this. One instance per value;
// the checkmark swap is local to whichever button was clicked.
//
// tabindex="-1": this whole workbench is built keyboard-first (T/F/U
// verdicts, j/k record navigation, native Tab reaching the verdict controls
// right after the hit records). A copy button on every single identity
// value would otherwise inject 8-10+ extra Tab stops between the hit
// header and the actual review controls. Mouse/click access still works
// fine with tabindex="-1" — only the natural Tab sequence skips these.
// An empty value has nothing to copy, so it gets no button at all.
const props = defineProps<{ value: string | undefined; label?: string }>();

const copied = ref(false);
let resetTimer: ReturnType<typeof setTimeout> | undefined;

async function copy() {
  if (!props.value) {
    return;
  }
  await navigator.clipboard.writeText(props.value);
  copied.value = true;
  clearTimeout(resetTimer);
  resetTimer = setTimeout(() => {
    copied.value = false;
  }, 1500);
}
</script>

<template>
  <button
    v-if="value"
    type="button"
    tabindex="-1"
    class="inline-flex flex-none items-center justify-center text-gray-400 hover:text-gray-600"
    data-test="copy-button"
    :aria-label="label ? `Copy ${label}` : 'Copy'"
    :title="copied ? 'Copied' : label ? `Copy ${label}` : 'Copy'"
    @click="copy"
  >
    <ClipboardDocumentCheckIcon v-if="copied" class="size-3.5 text-green-600" />
    <ClipboardDocumentIcon v-else class="size-3.5" />
  </button>
</template>
